import type { Attachment } from '@/lib/chat-types'

/** The exact six PNGs used by attach_soku_creatives in the prod Demo Agency
 * Soku creative thread. Read from the production onboarding-guide artifact:
 * brands/a7447e1a-5288-4d52-9c3d-6c30661b0ff4/chat-artifacts/
 * 8a52501a-55e8-47e8-90f3-21892d42d09b_soku-onboarding-guide.html
 * Extraction/order matches scripts/ops/seed_demo_org.py::_fetch_soku_creatives.
 * No generated substitutes, resizing, or invented performance metrics.
 */
const imageFiles = [
  {
    name: 'soku-ad-editorial-growth-by-hand.png',
    size: 1155976,
    sha256: '50f57f5a2d611107840aa34f6e231b7d65c8bcadcc39b64228bf7a33e66aedec',
  },
  {
    name: 'soku-ad-editorial-still-running-by-hand.png',
    size: 1154525,
    sha256: '16138521c788d8b6c0b5e01c14dd3db687fa699cdc4274c8a87b9519b0a8c8ae',
  },
  {
    name: 'soku-ad-editorial-autopilot-24-7.png',
    size: 1095085,
    sha256: 'c5526536ae7d78c8f66f126183d6c10779b05bd475bd7bb22c3f50841ccfbf00',
  },
  {
    name: 'soku-ad-proof-put-growth-on-autopilot.png',
    size: 1157602,
    sha256: 'db4b101f088221e6a6cf4935eff157d6627f8fb822943ccc470e9a5ba5a84296',
  },
  {
    name: 'soku-ad-testimonial-growth-teams-trust-soku.png',
    size: 1170045,
    sha256: '2dbc68533191c7f41bf2ad64e9a7d385aafc6a106c4d4b93466361ff99154fdc',
  },
  {
    name: 'soku-ad-lifestyle-put-growth-on-autopilot.png',
    size: 1287323,
    sha256: '023a4bfc4bfb95d0d023b724ea62b7d4004c635ca6cffcedbad51c594ecde8ec',
  },
] as const

function attachment(
  name: string,
  size: number,
  contentType: string,
): Attachment {
  return {
    id: `hero-${name}`,
    name,
    file_key: `marketing/demo-soku/${name}`,
    file_url: `/marketing/demo-soku/${name}`,
    type: contentType.startsWith('image/') ? 'image' : 'file',
    size,
    content_type: contentType,
    status: 'active',
    source: 'agent_attach',
    created_at: '', // Original attachment timestamp is not displayed in this preview.,
  }
}

export const sokuCreativeAttachments = imageFiles.map(file =>
  attachment(file.name, file.size, 'image/png'),
)

// Verbatim English replica of the July 3 production log, from
// scripts/ops/seed_demo_org.py::_SOKU_DUP_LOG_MD_EN.
export const campaignLogAttachment = attachment(
  'campaign_duplication_log.md',
  1475,
  'text/markdown',
)

// Production examples inspected read-only on 2026-09-10.
// Plan / deployment: 043c4116-baa3-4af5-9144-5694dbd9684e.
// Google diagnostic: 2b1d0817-77d6-42d0-b717-318fa6e22d8e.
// CPA monitor: 04f06f33-7f95-432e-be6a-9575a5259e51.
// XLSX and image captures are original bytes; HTML transformations are noted below.
// Markdown files are explicitly labeled editorial excerpts of those runs.
export const workflowAttachments = {
  mediaPlan: attachment(
    'meta_media_plan.xlsx',
    14504,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  ),
  diagnostic: attachment(
    'google-ads-diagnostic-2026-08-11.html',
    34902,
    'text/html',
  ),
  cpaDecision: attachment('cpa-monitor-decision.md', 867, 'text/markdown'),
  deployment: attachment('meta-deployment-summary.md', 792, 'text/markdown'),
  // Original production Birch report: 9e73cd41-e63f-4ab0-bb0c-3d45faa32b49.
  // English translation; preserve source charts, captures and evidence links; remove JS.
  research: attachment(
    'birch-competitor-intelligence-2026-08-20.html',
    44378,
    'text/html',
  ),
  searchAd: attachment('birch-search-ad.jpg', 28868, 'image/jpeg'),
  displayAd: attachment('birch-display-ad.jpg', 65874, 'image/jpeg'),
  // New editorial recommendations based on the report, explicitly not executed.
  researchBrief: attachment(
    'birch-response-test-brief.md',
    3314,
    'text/markdown',
  ),
}
